import { useEffect, useMemo, useState } from "react";
import { BookOpen, Check, Bookmark, BookMarked, ListOrdered, Plus, Compass, Library } from "lucide-react";
import client from "../lib/api";
import BookShelf from "../components/BookShelf";
import "../components/learning.css";

/**
 * Learning. What to study, and the books that back it. Every topic and
 * every book is tied to something Klair heard in the person's own
 * words. Books can be saved to a shelf and marked as reading or read.
 * Nothing prescriptive.
 */
export default function LearningScreen({ product }) {
  const [report, setReport] = useState(null);
  const [railEntries, setRailEntries] = useState([]); // full entry views
  const [tab, setTab] = useState("discover"); // discover | shelf
  const [filter, setFilter] = useState("all");
  const [sequences, setSequences] = useState({}); // anchor -> ordered entries

  // title -> { id, state }, derived so the Discover view can tell at a
  // glance which books are already on the shelf.
  const rail = useMemo(() => {
    const map = {};
    railEntries.forEach((e) => { map[e.title] = { id: e.id, state: e.state }; });
    return map;
  }, [railEntries]);

  const loadRail = () => {
    client.get("/reading-rail")
      .then((r) => setRailEntries(r?.data?.entries || []))
      .catch(() => setRailEntries([]));
  };

  // Replace one entry in place (by id), or prepend it if new.
  const upsertEntry = (data) => {
    if (!data || !data.id) return;
    setRailEntries((prev) => {
      const i = prev.findIndex((e) => e.id === data.id);
      if (i === -1) return [data, ...prev];
      const next = prev.slice();
      next[i] = data;
      return next;
    });
  };

  useEffect(() => {
    const raw = localStorage.getItem("klareet.report");
    if (raw) {
      try { setReport(JSON.parse(raw)); } catch (_e) { /* ignore */ }
    } else {
      client.get(`/demo/read/latest?product=${product}`)
        .then((r) => {
          const fetched = r?.data?.report;
          if (fetched) {
            setReport(fetched);
            try { localStorage.setItem("klareet.report", JSON.stringify(fetched)); } catch (_e) { /* ignore */ }
          }
        })
        .catch(() => {});
    }
    loadRail();
  }, [product]);

  const books = useMemo(() => (report?.books || []), [report]);

  const anchors = useMemo(() => {
    const set = new Set();
    books.forEach((b) => { if (b.anchor) set.add(b.anchor); });
    return Array.from(set);
  }, [books]);

  // KLA-044: the reading sequence behind each suggested anchor.
  useEffect(() => {
    if (!anchors.length) { setSequences({}); return; }
    client.get(`/reading-rail/catalogue?product=${product}&anchors=${encodeURIComponent(anchors.join(","))}`)
      .then((r) => setSequences(r?.data?.sequences || {}))
      .catch(() => setSequences({}));
  }, [anchors, product]);

  const shownBooks = filter === "all" ? books : books.filter((b) => b.anchor === filter);

  const saveBook = async (b) => {
    if (rail[b.title]) return;
    try {
      const { data } = await client.post("/reading-rail", {
        title: b.title, author: b.author || "Unknown",
        why: b.why || "", anchor: b.anchor || "", cover_url: b.cover_url || "",
      });
      upsertEntry(data);
    } catch (_e) { /* soft */ }
  };

  const setStateById = async (id, state) => {
    setRailEntries((prev) => prev.map((e) => (e.id === id ? { ...e, state } : e)));
    try {
      const { data } = await client.patch(`/reading-rail/${id}`, { state });
      upsertEntry(data);
    } catch (_e) { loadRail(); }
  };

  // The Discover view still marks state by book title.
  const setState = async (title, state) => {
    const entry = railEntries.find((e) => e.title === title);
    if (entry) await setStateById(entry.id, state);
  };

  const toggleFavorite = async (id, favorite) => {
    setRailEntries((prev) => prev.map((e) => (e.id === id ? { ...e, favorite } : e)));
    try {
      const { data } = await client.patch(`/reading-rail/${id}/favorite`, { favorite });
      upsertEntry(data);
    } catch (_e) { loadRail(); }
  };

  const saveQuote = async (id, quote) => {
    const { data } = await client.patch(`/reading-rail/${id}/quote`, { quote });
    upsertEntry(data);
    return data;
  };

  const generateSynopsis = async (id, refresh = false) => {
    const { data } = await client.post(`/reading-rail/${id}/synopsis${refresh ? "?refresh=true" : ""}`);
    upsertEntry(data);
    return data;
  };

  const removeBook = async (id) => {
    await client.delete(`/reading-rail/${id}`);
    setRailEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const shelfCount = railEntries.length;

  return (
    <div className="max-w-4xl mx-auto px-5 pt-8 pb-24">
      <div className="k-fade-in">
        <span className="lw-eyebrow" data-testid="learning-eyebrow"><BookOpen size={14} /> Learning</span>
        <h1 className="klareet-serif mt-3 mb-2" style={{ fontSize: "clamp(1.7rem, 4vw, 2.3rem)", lineHeight: 1.12 }} data-testid="learning-title">
          What to <span className="lw-mark">read next.</span>
        </h1>
        <p className="k-hero-sub mb-6" style={{ maxWidth: "56ch" }}>Every book here is tied to something Klair heard in your own words. Try what fits, skip what does not.</p>
      </div>

      {/* Discover / My Shelf */}
      <div className="k-tablist flex gap-2 mb-8" role="tablist" data-testid="learning-tabs">
        <button
          role="tab"
          aria-selected={tab === "discover"}
          data-state={tab === "discover" ? "active" : "inactive"}
          className="k-tab inline-flex items-center gap-1.5"
          onClick={() => setTab("discover")}
          data-testid="learning-tab-discover"
        >
          <Compass size={15} /> Discover
        </button>
        <button
          role="tab"
          aria-selected={tab === "shelf"}
          data-state={tab === "shelf" ? "active" : "inactive"}
          className="k-tab inline-flex items-center gap-1.5"
          onClick={() => setTab("shelf")}
          data-testid="learning-tab-shelf"
        >
          <Library size={15} /> My Shelf{shelfCount > 0 ? ` (${shelfCount})` : ""}
        </button>
      </div>

      {tab === "shelf" && (
        <div className="k-fade-in">
          <p className="k-quiet !text-sm sh-intro mb-6">
            Every book you save lands here. Tap a spine to open it: get a short synopsis, mark where you are, star a favourite, or write down a line worth keeping.
          </p>
          <BookShelf
            entries={railEntries}
            onSetState={setStateById}
            onToggleFavorite={toggleFavorite}
            onSaveQuote={saveQuote}
            onGenerateSynopsis={generateSynopsis}
            onRemove={removeBook}
          />
        </div>
      )}

      {tab === "discover" && !report && (
        <div className="text-center py-16" data-testid="learning-empty">
          <BookOpen size={40} className="mx-auto mb-3" style={{ color: "hsl(var(--klareet-muted))" }} />
          <h2 className="klareet-serif mb-2" style={{ fontSize: "1.5rem" }}>No learning plan yet</h2>
          <p className="k-quiet">Finish a conversation to see what to study next. Books you save still wait for you on My Shelf.</p>
        </div>
      )}

      {tab === "discover" && report && (
      <>
      {/* Suggested reading */}
      {books.length > 0 && (
        <div data-testid="learning-books">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="k-label !mb-0">suggested reading</div>
            {anchors.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="learning-filters">
                <FilterChip label="All" active={filter === "all"} onClick={() => setFilter("all")} tid="learning-filter-all" />
                {anchors.map((a) => (
                  <FilterChip key={a} label={a} active={filter === a} onClick={() => setFilter(a)} tid={`learning-filter-${a}`} />
                ))}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {shownBooks.map((b, i) => {
              const entry = rail[b.title];
              return (
                <div key={`${b.title}-${i}`} className="lw-card flex gap-4 p-4" data-testid={`book-card-${i}`}>
                  <BookCover book={b} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-snug" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`book-title-${i}`}>{b.title}</div>
                    {b.author && <div className="k-quiet !text-xs mb-1">{b.author}</div>}
                    {b.anchor && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] mb-2" style={{ background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-muted))" }}>
                        {b.anchor}
                      </span>
                    )}
                    {b.why && (
                      <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.5 }} data-testid={`book-why-${i}`}>
                        <span className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>Why this: </span>{b.why}
                      </p>
                    )}
                    <div className="mt-3">
                      {!entry ? (
                        <button onClick={() => saveBook(b)} className="k-btn k-btn-outline !py-1.5 !px-3 !text-xs" data-testid={`book-save-${i}`}>
                          <Bookmark size={13} /> Save to shelf
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5" data-testid={`book-states-${i}`}>
                          <StateBtn label="Want" state="want_to_read" current={entry.state} onClick={() => setState(b.title, "want_to_read")} tid={`book-want-${i}`} />
                          <StateBtn label="Reading" state="reading" current={entry.state} onClick={() => setState(b.title, "reading")} tid={`book-reading-${i}`} />
                          <StateBtn label="Read" state="read" current={entry.state} onClick={() => setState(b.title, "read")} tid={`book-read-${i}`} icon={<Check size={11} />} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KLA-044: read in this order. */}
      {Object.keys(sequences).length > 0 && (
        <div className="mt-10" data-testid="learning-sequences">
          <div className="k-label mb-1 flex items-center gap-2"><ListOrdered size={13} /> go deeper, in order</div>
          <p className="k-quiet !text-sm mb-4" style={{ maxWidth: 560, lineHeight: 1.5 }}>
            Each side of you has a small reading road. Start at the top, and one book at a time is plenty.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(sequences).map(([anchor, seq]) => (
              <div key={anchor} className="lw-card p-4" data-testid={`sequence-${anchor}`}>
                <div className="k-label mb-2">{anchor.replaceAll("_", " ")}</div>
                <ol className="space-y-2">
                  {seq.slice(0, 4).map((b, i) => {
                    const entry = rail[b.title];
                    return (
                      <li key={b.title} className="flex items-start gap-2.5" data-testid={`sequence-${anchor}-${i}`}>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 mt-0.5" style={{ background: "hsl(var(--klareet-teal) / 0.13)", color: "hsl(var(--klareet-teal))" }}>{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium leading-snug" style={{ color: "hsl(var(--klareet-ink))" }}>{b.title}</div>
                          <div className="k-quiet !text-xs">{b.author}</div>
                        </div>
                        {!entry ? (
                          <button onClick={() => saveBook(b)} className="k-btn k-btn-ghost !p-1.5 !px-1.5 shrink-0" title="Save to shelf" data-testid={`sequence-save-${anchor}-${i}`}>
                            <Bookmark size={13} />
                          </button>
                        ) : (
                          <Check size={14} className="shrink-0 mt-1" style={{ color: "hsl(var(--klareet-teal))" }} />
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddOwnBookCard
        onAdded={(entry) => upsertEntry(entry)}
      />
      </>
      )}
    </div>
  );
}


/**
 * KLA-044: the person's own picks. Any book they are reading joins the
 * shelf with the same statuses as Klair's suggestions.
 */
function AddOwnBookCard({ onAdded }) {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const add = async () => {
    if (!title.trim() || !author.trim() || busy) return;
    setBusy(true); setNote("");
    try {
      const { data } = await client.post("/reading-rail", {
        title: title.trim(), author: author.trim(),
        why: "your own pick", anchor: "my_pick",
      });
      if (onAdded && data?.id) onAdded(data);
      setNote(`Added. Find it on your shelf, and mark it reading when you start.`);
      setTitle(""); setAuthor("");
    } catch (_e) {
      setNote("That did not save. Check the title and author and try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="lw-card p-5 mt-10" data-testid="add-own-book-card">
      <div className="k-label mb-1 flex items-center gap-2"><Plus size={13} /> reading something of your own?</div>
      <p className="k-quiet !text-sm mb-4" style={{ maxWidth: 520, lineHeight: 1.5 }}>
        Add any book to your shelf and track it the same way. Your picks count just as much as Klair's.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <input className="k-input" placeholder="book title" maxLength={160} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="own-book-title" />
        <input className="k-input" placeholder="author" maxLength={120} value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="own-book-author" />
      </div>
      <button onClick={add} disabled={!title.trim() || !author.trim() || busy} className="k-btn k-btn-primary !text-sm" data-testid="own-book-add">
        {busy ? "Adding." : "Add to my shelf"}
      </button>
      {note && <div className="k-quiet !text-xs mt-3" data-testid="own-book-note">{note}</div>}
    </div>
  );
}


function FilterChip({ label, active, onClick, tid }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
      style={active
        ? { background: "hsl(var(--klareet-teal))", color: "white" }
        : { background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-ink-soft))", border: "1px solid hsl(var(--klareet-line-soft))" }}
      data-testid={tid}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}


function StateBtn({ label, state, current, onClick, tid, icon }) {
  const active = current === state;
  return (
    <button
      onClick={onClick}
      className="px-2.5 py-1 rounded-full text-[11px] font-medium inline-flex items-center gap-1 transition-colors"
      style={active
        ? { background: "hsl(var(--klareet-teal))", color: "white" }
        : { background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-ink-soft))", border: "1px solid hsl(var(--klareet-line-soft))" }}
      data-testid={tid}
      aria-pressed={active}
    >
      {icon}{label}
    </button>
  );
}


function BookCover({ book }) {
  const [broken, setBroken] = useState(false);
  if (book.cover_url && !broken) {
    return (
      <img
        src={book.cover_url}
        alt={book.title}
        onError={() => setBroken(true)}
        className="w-16 h-24 object-cover rounded-md shrink-0 shadow-sm"
        data-testid="book-cover"
      />
    );
  }
  return (
    <div className="w-16 h-24 rounded-md shrink-0 flex items-center justify-center" style={{ background: "hsl(var(--klareet-blue))", color: "white" }} data-testid="book-cover-fallback">
      <BookMarked size={22} />
    </div>
  );
}
